import { useState, useCallback, useRef, useEffect } from 'react';
import { performOptimization, initializeOptimizationChunks, calculateOverallProgress, formatTime } from '../services/llmOptimizationService';
import { logEvent } from '../services/loggingService';
import type { OptimizationChunk, OptimizationResult, JobData } from '../types/optimization';

export const useOptimizationProcess = () => {
  const [optimizationResults, setOptimizationResults] = useState<OptimizationResult[]>([]);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationError, setOptimizationError] = useState<string | null>(null);
  const [optimizationChunks, setOptimizationChunks] = useState<OptimizationChunk[]>([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Refs for cleanup
  const chunkIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Timer effect
  useEffect(() => {
    if (isOptimizing) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOptimizing]);

  const FALLBACK_MODEL = 'groq-llama-3.1-8b-instant';

  const runOneAttempt = useCallback(async (
    model: string,
    resumeText: string,
    jobData: JobData,
    resumeId: string | null,
    preferences: any
  ): Promise<OptimizationResult> => {
    return performOptimization(
      resumeText,
      jobData,
      resumeId,
      setOptimizationChunks,
      chunkIntervalsRef,
      model,
      preferences
    );
  }, []);

  const hasValidOptimizedResume = useCallback((r: OptimizationResult | null): boolean => {
    if (!r) return false;
    const text = (r as any).optimizedResume || (r as any).optimizedContent || (r as any).optimizedText || '';
    return typeof text === 'string' && text.trim().length >= 200;
  }, []);

  // Start optimization: retry once on failure, then try fallback model so we deliver a resume
  const startOptimization = useCallback(async (
    resumeText: string,
    jobData: JobData,
    resumeId: string | null = null,
    selectedModel: string = 'groq-llama-3.3-70b-versatile',
    preferences: any = {}
  ) => {
    try {
      setIsOptimizing(true);
      setOptimizationError(null);
      setElapsedTime(0);
      setOptimizationResults([]);

      const chunks = initializeOptimizationChunks();
      setOptimizationChunks(chunks);

      let result: OptimizationResult | null = null;
      let lastError: Error | null = null;

      // Attempt 1: selected model
      try {
        result = await runOneAttempt(selectedModel, resumeText, jobData, resumeId, preferences);
        if (hasValidOptimizedResume(result)) {
          setOptimizationResults([{ ...result, optimization_id: result.optimization_id || `opt_${Date.now()}_${Math.random().toString(36).substring(2, 15)}` }]);
          await logEvent({ level: 'info', action: 'single_optimization_complete', details: { jobTitle: jobData.title, company: jobData.company, optimizationId: result.optimization_id } });
          return;
        }
      } catch (e) {
        lastError = e instanceof Error ? e : new Error(String(e));
        console.warn('[Optimization] First attempt failed:', lastError.message);
      }

      // Attempt 2: retry same model
      chunkIntervalsRef.current.forEach(interval => clearInterval(interval));
      chunkIntervalsRef.current.clear();
      setOptimizationChunks(initializeOptimizationChunks());
      try {
        result = await runOneAttempt(selectedModel, resumeText, jobData, resumeId, preferences);
        if (hasValidOptimizedResume(result)) {
          setOptimizationResults([{ ...result, optimization_id: result.optimization_id || `opt_${Date.now()}_${Math.random().toString(36).substring(2, 15)}` }]);
          await logEvent({ level: 'info', action: 'single_optimization_complete', details: { jobTitle: jobData.title, company: jobData.company, retried: true } });
          return;
        }
      } catch (e) {
        lastError = e instanceof Error ? e : new Error(String(e));
        console.warn('[Optimization] Retry failed:', lastError.message);
      }

      // Attempt 3: fallback Groq model (free, so we can always try to deliver a resume)
      if (selectedModel !== FALLBACK_MODEL) {
        chunkIntervalsRef.current.forEach(interval => clearInterval(interval));
        chunkIntervalsRef.current.clear();
        setOptimizationChunks(initializeOptimizationChunks());
        try {
          console.log('[Optimization] Trying fallback model:', FALLBACK_MODEL);
          result = await runOneAttempt(FALLBACK_MODEL, resumeText, jobData, resumeId, preferences);
          if (hasValidOptimizedResume(result)) {
            const withFallback = { ...result, model: result.model || FALLBACK_MODEL, optimization_id: result.optimization_id || `opt_${Date.now()}_${Math.random().toString(36).substring(2, 15)}` };
            setOptimizationResults([withFallback]);
            await logEvent({ level: 'info', action: 'single_optimization_complete', details: { jobTitle: jobData.title, company: jobData.company, usedFallback: true } });
            return;
          }
        } catch (e) {
          lastError = e instanceof Error ? e : new Error(String(e));
          console.warn('[Optimization] Fallback failed:', lastError.message);
        }
      }

      setOptimizationError(lastError?.message || 'Optimization failed');
      await logEvent({
        level: 'error',
        action: 'single_optimization_failed',
        details: { jobTitle: jobData.title, error: lastError?.message || String(lastError) },
        error: lastError || new Error('Optimization failed')
      });
    } finally {
      setIsOptimizing(false);
    }
  }, [runOneAttempt, hasValidOptimizedResume]);

  // Reset optimization
  const resetOptimization = useCallback(() => {
    // Clear all intervals
    chunkIntervalsRef.current.forEach(interval => clearInterval(interval));
    chunkIntervalsRef.current.clear();
    
    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setOptimizationResults([]);
    setIsOptimizing(false);
    setOptimizationError(null);
    setOptimizationChunks([]);
    setElapsedTime(0);

    logEvent({
      level: 'info',
      action: 'optimization_reset',
      details: {}
    });
  }, []);

  // Calculate overall progress
  const getOverallProgress = useCallback(() => {
    return calculateOverallProgress(optimizationChunks);
  }, [optimizationChunks]);

  // Format time helper
  const getFormattedTime = useCallback((seconds: number) => {
    return formatTime(seconds);
  }, []);

  return {
    // State
    optimizationResults,
    isOptimizing,
    optimizationError,
    optimizationChunks,
    elapsedTime,
    
    // Actions
    startOptimization,
    resetOptimization,
    
    // Computed values
    hasResults: optimizationResults.length > 0,
    currentResult: optimizationResults[0] || null,
    calculateOverallProgress: getOverallProgress,
    formatTime: getFormattedTime,
    
    // Progress helpers
    isComplete: !isOptimizing && optimizationResults.length > 0,
    hasError: !!optimizationError
  };
};
